<template>
  <!-- 报备记录Drawer -->
  <a-drawer
    :width="900"
    :visible="drawerVisible"
    :unmount-on-close="true"
    :mask-closable="false"
    hide-cancel
    ok-text="关闭"
    @ok="closeDrawer"
    @cancel="closeDrawer"
  >
    <template #title>
      <a-space>
        {{
          `${customerInfo?.nickname}的${$t('report.records')}` ||
          $t('report.records')
        }}
        <a-tooltip content="添加报备记录">
          <a-button type="primary" size="mini" @click="showAddModal">
            <template #icon>
              <icon-plus />
            </template>
            添加报备记录
          </a-button>
        </a-tooltip>
      </a-space>
    </template>

    <a-table
      :columns="reportColumns"
      :data="reportData"
      :pagination="reportPagination"
      :loading="loading"
      :scroll="{ x: 1600 }"
      @page-change="handlePageChange"
    >
      <template #index="{ rowIndex }">
        {{
          rowIndex +
          1 +
          (reportPagination.current - 1) * reportPagination.pageSize
        }}
      </template>
      <template #imageArray="{ record }">
        <a-image-preview-group>
          <a-space>
            <a-image
              v-for="image in record.imageArray"
              :key="image"
              width="32"
              :src="image"
            />
          </a-space>
        </a-image-preview-group>
      </template>
      <template #state="{ record }">
        <a-tag :color="getStateColor(record.state)">
          {{ getStateText(record.state) }}
        </a-tag>
      </template>
      <template #operations="{ record }">
        <a-button type="primary" size="mini" @click="handleMsgRecord(record)">
          {{ $t('form.columns.operations.msgRecord') }}
        </a-button>
        <!-- <a-button type="text" size="small">
          <icon-edit />
        </a-button> -->
      </template>
    </a-table>
  </a-drawer>

  <!-- 添加报备Modal -->
  <a-modal
    :visible="addReportVisible"
    :mask-closable="false"
    title="添加报备记录"
    unmount-on-close
    @before-ok="handleBeforeOk"
    @cancel="handleModalCancel"
  >
    <a-form :model="formData">
      <a-form-item field="name" :label="$t('form.name')">
        <a-input
          v-model="formData.name"
          :placeholder="$t('form.name.placeholder')"
        />
      </a-form-item>
      <a-form-item field="mobile" :label="$t('form.mobile')">
        <a-input
          v-model="formData.mobile"
          :placeholder="$t('form.mobile.placeholder')"
        />
      </a-form-item>
      <a-form-item field="project" :label="$t('report.project')">
        <a-select
          v-model="formData.selectedProjectId"
          :options="projectParentOptions"
          placeholder="请选择项目分类"
          allow-search
          @change="handleProjectParentChange"
        />
      </a-form-item>
      <a-form-item
        v-if="formData.selectedProjectId"
        field="projectChild"
        label="意向项目"
      >
        <a-select
          v-model="selectedProjectChildId"
          :options="projectChildOptions"
          placeholder="请选择具体项目"
          allow-search
          @change="handleProjectChildChange"
        />
      </a-form-item>
      <a-form-item field="city" :label="$t('report.city')">
        <a-select
          v-model="formData.city"
          :options="cityOptions"
          placeholder="请选择城市"
          allow-search
          @change="handleCityChange"
        />
      </a-form-item>
      <a-form-item field="hospital" :label="$t('report.hospital')">
        <a-select
          v-model="formData.cid"
          :options="hospitalOptions"
          placeholder="请选择医院"
          allow-search
          multiple
          limit="5"
          @change="handleHospitalChange"
        >
          <template #option="{ data }">
            <span
              :style="{
                color: data.disabled ? '#c9cdd4' : 'inherit',
                cursor: data.disabled ? 'not-allowed' : 'pointer',
              }"
            >
              {{ data.label }}
            </span>
          </template>
        </a-select>
      </a-form-item>
      <a-form-item field="msg" :label="$t('report.msg')">
        <a-input
          v-model="formData.msg"
          :placeholder="$t('report.msg.placeholder')"
        />
      </a-form-item>
      <a-form-item field="is_flow" :label="$t('report.flow')">
        <a-select
          v-model="formData.is_flow"
          :placeholder="$t('report.is_flow.placeholder')"
        >
          <a-option :value="1" :label="$t('report.is_flow.no_contact')" />
          <a-option :value="0" :label="$t('report.is_flow.immediate')" />
          <a-option :value="3" :label="$t('report.is_flow.three_days')" />
          <a-option :value="7" :label="$t('report.is_flow.seven_days')" />
        </a-select>
      </a-form-item>
      <a-form-item field="images" label="上传图片">
        <a-upload
          list-type="picture-card"
          tip="上传图片"
          :action="uploadImageUrl"
          :file-list="fileList"
          :on-before-upload="beforeUpload"
          :accept="['image/jpeg', 'image/png', 'image/gif', 'image/webp']"
          :limit="5"
          image-preview
          :headers="{ token: userStore.token }"
          @success="handleSuccess"
          @remove="handleImageRemove"
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, reactive, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Message } from '@arco-design/web-vue';
  import {
    addReport,
    queryReportList,
    type AddReportParams,
    type ReportRecord,
    type ReportParams,
    queryCityList,
    queryReportProjects,
    queryReportHospitals,
    type HospitalParams,
    type HospitalRecord,
    type ReportProject,
    type ReportProjectChild,
    type ReportHospitalRecord,
    MessageInfo,
    queryCustomerReportList,
  } from '@/api/report';
  import type { BaseCustomer } from '@/types/customer';
  import { useUserStore, useDrawerStore } from '@/store';
  import { stringToArray } from '@/utils/utils';

  const userStore = useUserStore();
  const drawerStore = useDrawerStore();

  // 报备状态枚举
  const ReportState = {
    PROCESSING: 1, // 待处理
    EXSIST: 2, // 重单
    EXSISTDEEP: 3, // 重单可深度
    NOTEXSIST: 4, // 不重单
  } as const;

  // 状态文本映射
  const stateTextMap: Record<number, string> = {
    [ReportState.PROCESSING]: '待处理',
    [ReportState.EXSIST]: '重单',
    [ReportState.EXSISTDEEP]: '重单可深度',
    [ReportState.NOTEXSIST]: '不重单',
  };

  // 状态颜色映射
  const stateColorMap: Record<number, string> = {
    [ReportState.PROCESSING]: 'blue',
    [ReportState.EXSIST]: 'orange',
    [ReportState.EXSISTDEEP]: 'green',
    [ReportState.NOTEXSIST]: 'red',
  };

  // 获取状态文本
  const getStateText = (state: number): string => {
    return stateTextMap[state] || '未知状态';
  };

  // 获取状态颜色
  const getStateColor = (state: number): string => {
    return stateColorMap[state] || 'gray';
  };

  interface Props {
    drawerVisible: boolean;
    customerInfo?: BaseCustomer;
  }

  interface Emits {
    (e: 'update:drawerVisible', value: boolean): void;
    (e: 'success'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();
  const { t } = useI18n();

  // 添加报备Modal状态
  const addReportVisible = ref(false);
  const loading = ref(false);

  // 报备表格数据
  const reportData = ref<ReportRecord[]>([]);
  const reportPagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 表格列定义
  const reportColumns = computed(() => [
    {
      title: '序号',
      slotName: 'index',
      width: 50,
    },
    {
      title: t('form.name'),
      dataIndex: 'name',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('form.mobile'),
      dataIndex: 'customer_info.mobile',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.state'),
      dataIndex: 'state',
      slotName: 'state',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.project'),
      dataIndex: 'project',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('report.city'),
      dataIndex: 'city',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.hospital'),
      dataIndex: 'hospital_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('report.msg'),
      dataIndex: 'note.content',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('report.form.images'),
      dataIndex: 'imageArray',
      width: 120,
      slotName: 'imageArray',
    },
    {
      title: t('report.form.state.create_time'),
      dataIndex: 'times',
      width: 200,
      ellipsis: true,
    },
    {
      title: '操作',
      dataIndex: 'operations',
      slotName: 'operations',
      width: 100,
      fixed: 'right',
    },
  ]);

  // 添加报备表单数据
  const formData = reactive<
    AddReportParams & {
      selectedProjectId: number | undefined;
      projects: string;
    }
  >({
    cid: [],
    project: '',
    city: '',
    name: props.customerInfo?.nickname || '',
    mobile: props.customerInfo?.mobile || '',
    images: '',
    msg: '',
    is_privacy: 0,
    uid: 0,
    uuid: 0,
    selectedProjectId: undefined,
    projects: '',
    is_flow: 1,
  });

  // 城市和医院选项
  const cityOptions = ref<{ label: string; value: string }[]>([]);
  const hospitalOptions = ref<{ label: string; value: number }[]>([]);

  // 已提交过的医院ID集合（用于禁用重复选择）
  const submittedHospitalIds = ref<Set<number>>(new Set());

  // 项目选项（两级结构）
  const projectList = ref<ReportProject[]>([]);
  const projectParentOptions = computed(() =>
    projectList.value.map((item) => ({
      label: item.name,
      value: item.id,
    }))
  );
  const projectChildOptions = computed(() => {
    if (!formData.selectedProjectId) return [];
    const parent = projectList.value.find(
      (item) => item.id === formData.selectedProjectId
    );
    return parent
      ? parent.child_list.map((child) => ({
          label: child.name,
          value: child.id,
        }))
      : [];
  });

  // 临时存储选中的子项目ID（用于模板绑定）
  const selectedProjectChildId = ref<number | undefined>(undefined);

  // 图片上传相关
  const fileList = ref<
    Array<{
      uid: string;
      name: string;
      url?: string;
      status: 'done' | 'uploading' | 'error';
    }>
  >([]);

  const uploadImageUrl = `${
    import.meta.env.VITE_API_BASE_URL
  }/tool/upload_image`;

  const beforeUpload = (file: File) => {
    const isPic = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isPic) {
      Message.error('只能上传 JPG 或 PNG 格式的图片');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      Message.error('图片大小不能超过 2MB');
    }
    return isPic && isLt2M;
  };

  const handleSuccess = (response: any) => {
    Message.success('上传成功');
    fileList.value.push({
      uid: String(Date.now()),
      name: response.name || 'image',
      url: response.response?.data?.url || response.url,
      status: 'done',
    });
  };

  // 更新formData.images字段
  const updateFormImages = () => {
    formData.images = fileList.value
      .filter((file) => file.url)
      .map((file) => file.url)
      .join(',');
  };

  // 删除图片
  const handleImageRemove = (file: any) => {
    const fileIndex = fileList.value.findIndex((f) => f.uid === file.uid);
    if (fileIndex !== -1) {
      fileList.value.splice(fileIndex, 1);
      updateFormImages();
    }
  };

  // 获取城市列表
  const fetchCities = async () => {
    try {
      const res = await queryCityList();
      cityOptions.value = (res.data as unknown as string[]).map(
        (city: string) => ({
          label: city,
          value: city,
        })
      );
    } catch (error) {
      console.error('获取城市列表失败:', error);
    }
  };

  // 获取报备项目列表
  const fetchProjects = async () => {
    try {
      const res = await queryReportProjects();
      projectList.value = res.data;
    } catch (error) {
      console.error('获取项目列表失败:', error);
    }
  };

  // 获取医院列表（根据选中的项目和城市）
  const fetchHospitals = async () => {
    if (!formData.city || !formData.selectedProjectId) {
      hospitalOptions.value = [];
      return;
    }

    try {
      // 使用一级分类ID（父级ID）请求医院列表
      const res = await queryReportHospitals({
        ids: [formData.selectedProjectId],
        city: formData.city,
      });
      // 合并 level_1 和 level_2 数据
      const allHospitals = [...res.data.level_1, ...res.data.level_2];
      hospitalOptions.value = allHospitals.map(
        (hospital: ReportHospitalRecord) => ({
          label: hospital.nickname,
          value: hospital.cid,
          disabled:
            !hospital.is_comply || submittedHospitalIds.value.has(hospital.cid),
        })
      );
    } catch (error) {
      console.error('获取医院列表失败:', error);
      hospitalOptions.value = [];
    }
  };

  // 获取报备记录列表
  const fetchReportList = async () => {
    const customerId = props.customerInfo?.user_id || props.customerInfo?.id;
    if (!customerId) return;

    loading.value = true;
    try {
      const params: ReportParams = {
        // state: 0,
        page: reportPagination.current,
        pageSize: reportPagination.pageSize,
        // mobile: props.customerInfo?.mobile,
        id: customerId,
        // uid: customerId,
      };
      const res = await queryCustomerReportList(params);
      reportData.value = res.data.list.map((item) => {
        item.imageArray = stringToArray(item.msg_images);
        return item;
      });
      // 提取所有已提交的医院ID
      submittedHospitalIds.value = new Set(
        res.data.list.flatMap((item) => {
          if (Array.isArray(item.cid)) return item.cid;
          return [item.cid] as number[];
        })
      );
      reportPagination.total = res.data.total;
    } catch (error) {
      console.error('获取报备记录失败:', error);
    } finally {
      loading.value = false;
    }
  };

  const closeDrawer = () => {
    emit('update:drawerVisible', false);
    setTimeout(() => {
      reportData.value = [];
      reportPagination.current = 1;
      reportPagination.total = 0;
      submittedHospitalIds.value.clear();
    }, 300);
  };

  const showAddModal = () => {
    // 重置表单数据为当前用户信息
    formData.name = props.customerInfo?.nickname || '';
    formData.mobile = props.customerInfo?.mobile || '';
    addReportVisible.value = true;
    fetchCities();
    // 获取项目列表（如果还没有获取）
    if (projectList.value.length === 0) {
      fetchProjects();
    }
  };

  const resetForm = () => {
    Object.assign(formData, {
      cid: [],
      project: '',
      city: '',
      name: props.customerInfo?.nickname || '',
      mobile: props.customerInfo?.mobile || '',
      images: '',
      msg: '',
      is_flow: 0,
      is_privacy: 0,
      uid: 0,
      uuid: 0,
      selectedProjectId: undefined,
      projects: '',
    });
    selectedProjectChildId.value = undefined;
    cityOptions.value = [];
    hospitalOptions.value = [];
    fileList.value = [];
    // 不重置项目列表，避免重复请求
  };

  const handleModalCancel = () => {
    addReportVisible.value = false;
    setTimeout(() => {
      resetForm();
    }, 300);
  };

  const handleCityChange = (city: string) => {
    formData.city = city;
    formData.cid = [];
    // 城市或项目变化时重新获取医院列表
    fetchHospitals();
  };

  const handleHospitalChange = (hospitalIds: number[]) => {
    formData.cid = hospitalIds;
  };

  // 项目父级分类选择变化
  const handleProjectParentChange = (parentId: number) => {
    formData.selectedProjectId = parentId;
    selectedProjectChildId.value = undefined;
    formData.projects = '';
    formData.project = '';
    formData.cid = [];
    hospitalOptions.value = [];
  };

  // 项目子项选择变化
  const handleProjectChildChange = (childId: number) => {
    selectedProjectChildId.value = childId;
    if (!formData.selectedProjectId) return;
    const parent = projectList.value.find(
      (item) => item.id === formData.selectedProjectId
    );
    const child = parent?.child_list.find((c) => c.id === childId);
    formData.projects = child?.name || '';
    formData.project = String(childId);
    formData.cid = [];
    fetchHospitals();
  };

  const handlePageChange = (page: number) => {
    reportPagination.current = page;
    fetchReportList();
  };

  const handleBeforeOk = async (done: (closed: boolean) => void) => {
    // 验证客户信息
    const customerId = props.customerInfo?.user_id || props.customerInfo?.id;
    if (!customerId) {
      Message.error('客户信息不完整');
      done(false); // 阻止关闭
      return;
    }

    // 验证表单必填项
    if (
      !formData.name ||
      !formData.mobile ||
      !formData.project ||
      !formData.city ||
      formData.cid.length === 0
    ) {
      Message.error('请填写完整信息（包括选择意向项目和医院）');
      done(false); // 阻止关闭
      return;
    }

    try {
      updateFormImages();
      const params: AddReportParams = {
        ...formData,
        uid: customerId,
        uuid: customerId,
      };

      await addReport(params);
      Message.success('添加报备记录成功');
      // 将新提交的医院ID加入已提交集合
      if (Array.isArray(formData.cid)) {
        formData.cid.forEach((id) =>
          submittedHospitalIds.value.add(Number(id))
        );
      } else {
        submittedHospitalIds.value.add(Number(formData.cid));
      }
      resetForm();
      fetchReportList();
      emit('success');
      done(true);
      handleModalCancel();
    } catch (error) {
      console.error('添加报备记录失败:', error);
      Message.error('添加报备记录失败');
      done(false); // 阻止关闭
    }
  };

  const handleMsgRecord = (record: ReportRecord) => {
    drawerStore.openReportMsgDrawer(record.id);
  };

  // 监听drawer显示状态，获取报备记录
  watch(
    () => props.drawerVisible,
    (visible) => {
      const customerId = props.customerInfo?.user_id || props.customerInfo?.id;
      if (visible && customerId) {
        fetchReportList();
      }
    },
    { immediate: true }
  );
</script>

<style scoped>
  /* 可以添加一些样式 */
</style>
